import { useParams, useNavigate, Link, useSearchParams, } from "react-router-dom";
import { useEffect, useState } from "react";

import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Separator } from "@/shared/components/ui/separator";


import { useJob } from "@/modules/jobs/hooks/useJob";
import { useDispatch, useSelector } from "react-redux";

import {
  openLoginDialog,
  clearPendingJob,
} from "@/modules/auth/store/authSlice";

import CandidateApplicationDialog from "@/modules/candidate/components/applications/candidate-application-dialog/CandidateApplicationDialog";

import AlreadyAppliedDialog from "@/shared/components/dialogs/AlreadyAppliedDialog";

import {
  formatEmploymentType,
  formatExperience,
  getPostedDate,
  getDeadline,
} from "@/modules/jobs/utils/jobFormatter";

import {
  ArrowLeft,
  MapPin,
  IndianRupee,
  GraduationCap,
  Users,
  CheckCircle2,
  Building2,
  Briefcase,
  Target,
  Lightbulb,
  Award,
} from "lucide-react";

// =========================================================
// SALARY FORMATTER
// =========================================================

const formatSalary = (
  minimumSalary: number | string | null | undefined,
  maximumSalary: number | string | null | undefined,
  currency?: string | null
) => {
  if (
    minimumSalary === null ||
    minimumSalary === undefined ||
    maximumSalary === null ||
    maximumSalary === undefined
  ) {
    return "Not specified";
  }

  const minimum = Number(minimumSalary);
  const maximum = Number(maximumSalary);

  if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) {
    return "Not specified";
  }

  const symbol = currency === "INR" ? "₹" : currency || "";

  const formatValue = (value: number) => {
    return Number.isInteger(value)
      ? String(value)
      : value
        .toFixed(2)
        .replace(/0+$/, "")
        .replace(/\.$/, "");
  };

  return `${symbol}${formatValue(minimum)} - ${symbol}${formatValue(
    maximum
  )} LPA`;
};

// =========================================================
// PAGE
// =========================================================

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const referralToken = searchParams.get("ref") || undefined;

  const referralUtm = {
    utm_source: searchParams.get("utm_source") || undefined,
    utm_medium: searchParams.get("utm_medium") || undefined,
    utm_campaign: searchParams.get("utm_campaign") || undefined,
    utm_content: searchParams.get("utm_content") || undefined,
    utm_term: searchParams.get("utm_term") || undefined,
  };

  const [applicationDialogOpen, setApplicationDialogOpen] =
    useState(false);

  const [applicationReferralToken, setApplicationReferralToken] =
    useState<string | undefined>();

  const [applicationReferralUtm, setApplicationReferralUtm] =
    useState<typeof referralUtm | undefined>();

  // HR / ADMIN access popup
  const [showAccessPopup, setShowAccessPopup] = useState(false);

  const { job, loading, error } = useJob(id);

  const dispatch: any = useDispatch();

  const {
    user,
    pendingJobId,
    pendingReferralToken,
    pendingReferralUtm,
  } = useSelector((state: any) => state.auth);

  // =========================================================
  // ROLE CHECK
  // =========================================================

  const isCandidate = () => {
    const role = String(user?.role || "").toUpperCase();

    return role === "CANDIDATE";
  };

  // =========================================================
  // OPEN APPLICATION DIALOG AFTER LOGIN
  // =========================================================

  useEffect(() => {
    if (
      user &&
      pendingJobId &&
      pendingJobId === job?.id
    ) {
      if (isCandidate()) {
        setApplicationReferralToken(
          pendingReferralToken || undefined
        );

        setApplicationReferralUtm(
          pendingReferralUtm || undefined
        );

        setApplicationDialogOpen(true);
      } else {
        setShowAccessPopup(true);
      }

      dispatch(clearPendingJob());
    }
  }, [
    user,
    pendingJobId,
    pendingReferralToken,
    pendingReferralUtm,
    job?.id,
    dispatch,
  ]);

  // =========================================================
  // APPLY NOW
  // =========================================================

  const handleApplyNow = () => {
    if (user) {
      if (!isCandidate()) {
        setShowAccessPopup(true);
        return;
      }

      setApplicationReferralToken(referralToken);
      setApplicationReferralUtm(referralUtm);

      setApplicationDialogOpen(true);
      return;
    }

    dispatch(
      openLoginDialog({
        mode: "login",
        jobId: job?.id,
        referralToken,
        referralUtm,
      })
    );
  };

  // =========================================================
  // ACCESS POPUP CLOSE
  // =========================================================

  const handleAccessPopupChange = (open: boolean) => {
    setShowAccessPopup(open);
  };

  // =========================================================
  // ERROR
  // =========================================================

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        {error}
      </div>
    );
  }

  // =========================================================
  // JOB NOT FOUND
  // =========================================================

  if (!job) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Briefcase className="mx-auto h-12 w-12 text-muted-foreground" />

          <h2 className="mt-4 text-xl font-bold">
            Job Not Found
          </h2>

          <p className="text-muted-foreground">
            The position you are looking for does not exist.
          </p>

          <Button
            className="mt-4"
            onClick={() => navigate("/")}
          >
            Back to Careers
          </Button>
        </div>
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-lg">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
                <Building2 className="h-5 w-5 text-white" />
              </div>

              <span className="text-xl font-bold tracking-tight">
                Dharohar Land Corp
              </span>
            </Link>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              All Jobs
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3">

          {/* =====================================================
              MAIN CONTENT
          ===================================================== */}

          <div className="space-y-6 lg:col-span-2">

            {/* Header Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      {job.priority === "HIGH" && (
                        <Badge variant="destructive">
                          Urgent
                        </Badge>
                      )}

                      <Badge variant="secondary">
                        {formatEmploymentType(
                          job.employmentType
                        )}
                      </Badge>
                    </div>

                    <h1 className="text-2xl font-bold tracking-tight">
                      {job.title}
                    </h1>

                    <p className="mt-1 text-muted-foreground">
                      {job.department}
                    </p>

                    {/* Job Code */}
                    <p className="mt-1 text-sm font-medium text-gray-500">
                      Job ID:{" "}
                      {job.jobCode?.replace(/^JOB-/, "")}
                    </p>
                  </div>
                </div>

                {/* Job Summary */}
                <div className="mt-6 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    {job.location}
                  </div>

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <GraduationCap className="h-4 w-4 text-blue-600" />
                    {formatExperience(job)}
                  </div>

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <IndianRupee className="h-4 w-4 text-blue-600" />

                    {formatSalary(
                      job.minimumSalary,
                      job.maximumSalary,
                      job.currency
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4 text-blue-600" />

                    {job.positions} Position
                    {job.positions > 1 ? "s" : ""}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  About the Role
                </CardTitle>
              </CardHeader>

              <CardContent>
                <p className="leading-relaxed text-muted-foreground">
                  {job.description}
                </p>
              </CardContent>
            </Card>

            {/* Responsibilities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Key Responsibilities
                </CardTitle>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {job.responsibilities.map(
                    (item: string, index: number) => (
                      <li
                        key={index}
                        className="flex items-start gap-3"
                      >
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />

                        <span className="text-muted-foreground">
                          {item}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                  Requirements
                </CardTitle>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {job.requirements.map(
                    (item: string, index: number) => (
                      <li
                        key={index}
                        className="flex items-start gap-3"
                      >
                        <div className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />

                        <span className="text-muted-foreground">
                          {item}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Skills */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-blue-600" />
                  Required Skills
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {job.skills.map(
                    (skill: string, index: number) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="px-3 py-1"
                      >
                        {skill}
                      </Badge>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* =====================================================
              SIDEBAR
          ===================================================== */}

          <div className="space-y-6">

            {/* Apply Card */}
            <Card className="relative border-0 bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold">
                  Interested in this role?
                </h3>

                <p className="mt-2 text-sm text-blue-100">
                  Apply now and our AI will screen your
                  application within minutes.
                </p>

                <Button
                  className="mt-4 w-full bg-white text-blue-600 hover:bg-blue-50"
                  size="lg"
                  onClick={handleApplyNow}
                >
                  Apply Now
                </Button>

                <p className="mt-3 text-center text-xs text-blue-200">
                  Posted {getPostedDate(job.createdAt)} |{" "}
                  {getDeadline(job.applicationDeadline)}
                </p>
              </CardContent>
            </Card>

            {/* Benefits */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-blue-600" />
                  Benefits
                </CardTitle>
              </CardHeader>

              <CardContent>
                <ul className="space-y-2">
                  {job.benefits.map(
                    (benefit: string, index: number) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        {benefit}
                      </li>
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Info</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Experience
                  </span>

                  <span className="font-medium">
                    {formatExperience(job)}
                  </span>
                </div>

                <Separator />

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Location
                  </span>

                  <span className="font-medium">
                    {job.location}
                  </span>
                </div>

                <Separator />

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Employment Type
                  </span>

                  <span className="font-medium">
                    {formatEmploymentType(
                      job.employmentType
                    )}
                  </span>
                </div>

                <Separator />

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Reporting To
                  </span>

                  <span className="font-medium">
                    {job.reportingManager}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* AI Screening Info */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-6">
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                    <svg
                      className="h-4 w-4 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                  </div>

                  <h4 className="font-semibold">
                    AI-Powered Screening
                  </h4>
                </div>

                <p className="text-xs text-muted-foreground">
                  Our AI will analyze your resume and video
                  introduction. Candidates scoring 75%+ proceed
                  to HR interview.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* =====================================================
          CANDIDATE APPLICATION DIALOG
      ===================================================== */}

      <CandidateApplicationDialog
        open={applicationDialogOpen}
        onOpenChange={setApplicationDialogOpen}
        jobId={job.id}
        jobTitle={job.title}
        referralToken={applicationReferralToken}
        referralUtm={applicationReferralUtm}
      />

      {/* =====================================================
          HR / ADMIN ACCESS POPUP
      ===================================================== */}

      <AlreadyAppliedDialog
        open={showAccessPopup}
        onOpenChange={handleAccessPopupChange}
        type="warning"
        title="Action Not Available"
        message="Only candidates can submit job applications. HR and Admin users can manage jobs and review candidate applications from their respective dashboards."
        buttonText="Got it"
        onViewApplicationStatus={() =>
          setShowAccessPopup(false)
        }
      />
    </div>
  );
}